// inactivityService.js
const GRACE_MS = 60 * 1000; // 60s to respond
const ACTIVITY_THROTTLE_MS = 1000;
const ELECTION_WINDOW_MS = 150;
const STORAGE_KEY = "app:lastActivity";
const WARNING_KEY = "app:warningStartedAt";

export default class InactivityService {
  constructor({ inactivityMinutes = 60, onShowDialog, onHideDialog, onTimeout }) {
    this.inactivityMs = inactivityMinutes * 60 * 1000;
    this.onShowDialog = onShowDialog;
    this.onHideDialog = onHideDialog;
    this.onTimeout = onTimeout;

    this.channel = new BroadcastChannel("app-activity");
    this.lastActivity = Date.now();
    this.lastBroadcast = 0;
    this.warningStartedAt = null; // null = no warning active
    this.dialogVisible = false;
    this.myBid = null;
    this.bids = [];
    this.electionTimer = null;
    this.checkInterval = null;

    // Clean up any stale warning state from a previous session.
    // If a real warning is in progress in another tab, we'll learn about it
    // via the 'warning-start' broadcast or the next tick's storage read
    // (but only if the timestamp is recent).
    const storedWarning = parseInt(
      localStorage.getItem(WARNING_KEY) || "0",
      10
    );
    if (storedWarning && Date.now() - storedWarning > GRACE_MS) {
      localStorage.removeItem(WARNING_KEY);
    }

    this.channel.onmessage = (e) => this.handleMessage(e.data);
    this.attachActivityListeners();
    this.startChecking();
    this.recordActivity();
  }

  // ---------- activity tracking (unchanged from before) ----------
  attachActivityListeners() {
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    this.activityHandler = () => {
      // Activity during warning is NOT auto-stay — user must click STAY.
      // Without this, just moving the mouse would dismiss the dialog,
      // which defeats the security purpose.
      if (this.warningStartedAt !== null) return;
      this.recordActivity();
    };
    events.forEach((e) =>
      window.addEventListener(e, this.activityHandler, { passive: true })
    );
  }

  recordActivity() {
    const now = Date.now();
    this.lastActivity = now;
    if (now - this.lastBroadcast > ACTIVITY_THROTTLE_MS) {
      this.lastBroadcast = now;
      this.channel.postMessage({ type: "activity", timestamp: now });
      localStorage.setItem(STORAGE_KEY, String(now));
    }
  }

  // ---------- main check loop ----------
  startChecking() {
    this.checkInterval = setInterval(() => this.tick(), 5 * 1000);
  }

  tick() {
    const now = Date.now();
    const stored = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    const effectiveLast = Math.max(this.lastActivity, stored);

    // Sync warning state from storage (a tab opened mid-warning needs to know)
    const storedWarning = parseInt(
      localStorage.getItem(WARNING_KEY) || "0",
      10
    );
    if (storedWarning && this.warningStartedAt === null) {
      // Only adopt if it's still within the grace window
      if (Date.now() - storedWarning < GRACE_MS) {
        this.warningStartedAt = storedWarning;
      } else {
        localStorage.removeItem(WARNING_KEY); // stale, clean up
      }
    }

    if (this.warningStartedAt !== null) {
      // In grace period — check if it expired
      if (now - this.warningStartedAt > GRACE_MS) {
        this.triggerLogout(true);
      }
    } else {
      // Normal mode — check if inactivity threshold crossed
      if (now - effectiveLast > this.inactivityMs) {
        this.startWarning(now);
      }
    }
  }

  // ---------- warning phase ----------
  startWarning(now) {
    this.warningStartedAt = now;
    localStorage.setItem(WARNING_KEY, String(now));
    this.channel.postMessage({ type: "warning-start", timestamp: now });
    this.runElection();
  }

  runElection() {
    if (document.hidden) return; // hidden tabs don't bid

    this.myBid = Math.random();
    this.bids = [{ id: "self", bid: this.myBid }];
    this.channel.postMessage({ type: "bid", bid: this.myBid });

    this.electionTimer = setTimeout(() => {
      const winner = this.bids.reduce((a, b) => (a.bid > b.bid ? a : b));
      if (winner.id === "self") {
        this.showDialog();
      }
    }, ELECTION_WINDOW_MS);
  }

  showDialog() {
    this.dialogVisible = true;
    this.onShowDialog({
      remainingMs: GRACE_MS - (Date.now() - this.warningStartedAt),
      onStay: () => this.handleStay(),
    });
  }

  handleStay() {
    this.channel.postMessage({ type: "stay" });
    this.resetWarning();
    this.recordActivity();
  }

  resetWarning() {
    this.warningStartedAt = null;
    localStorage.removeItem(WARNING_KEY);
    clearTimeout(this.electionTimer);
    if (this.dialogVisible) {
      this.dialogVisible = false;
      this.onHideDialog();
    }
  }

  triggerLogout(broadcast) {
    if (broadcast) this.channel.postMessage({ type: "logout" });
    localStorage.removeItem(WARNING_KEY);
    this.destroy();
    this.onTimeout();
  }

  // ---------- inbound messages ----------
  handleMessage(msg) {
    switch (msg.type) {
      case "activity":
        if (this.warningStartedAt === null) {
          this.lastActivity = Math.max(this.lastActivity, msg.timestamp);
        }
        break;

      case "warning-start":
        if (this.warningStartedAt === null) {
          this.warningStartedAt = msg.timestamp;
          this.runElection();
        }
        break;

      case "bid":
        this.bids.push({ id: "other", bid: msg.bid });
        break;

      case "stay":
        this.resetWarning();
        this.lastActivity = Date.now();
        break;

      case "logout":
        this.triggerLogout(false);
        break;
    }
  }

  destroy() {
    clearInterval(this.checkInterval);
    clearTimeout(this.electionTimer);
    this.channel.close();
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) =>
      window.removeEventListener(e, this.activityHandler, { passive: true })
    );
  }
}
